import type { Observable } from 'rxjs';
import type { EduCourse, EduCourseTariff, EduCourseWithTariffs } from '@common/types/edu-course.type';
import type { CollectionStateStatuses } from '@common/adapters/collections';
import type { EduCoursesCollectionState } from './edu-courses.reducer';

import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { map } from 'rxjs/operators';

import * as EduCoursesSelectors from './edu-courses.selectors';

@Injectable()
export class EduCoursesCollectionFacade {
    private readonly store = inject<Store<EduCoursesCollectionState>>(Store);

    public readonly eduCourses$: Observable<EduCourse[]> = this.store.select(EduCoursesSelectors.selectEduCourses);
    public readonly isEduCoursesEmpty$: Observable<boolean> = this.store.select(
        EduCoursesSelectors.selectIsEduCoursesEmpty
    );
    public readonly eduCoursesStatuses$: Observable<CollectionStateStatuses> = this.store.select(
        EduCoursesSelectors.selectEduCoursesStatuses
    );
    public readonly eduCoursesWithTariffs$: Observable<EduCourseWithTariffs[]> = this.store.select(
        EduCoursesSelectors.selectEduCoursesWithTariffs
    );

    public eduCoursesBySiteId$(siteId: string): Observable<EduCourse[]> {
        return this.store.select(EduCoursesSelectors.selectEduCoursesBySiteId(siteId));
    }

    public eduCoursesWithTariffsBySiteId$(siteId: string): Observable<EduCourseWithTariffs[]> {
        return this.store.select(EduCoursesSelectors.selectEduCoursesWithTariffsBySiteId(siteId));
    }

    public get eduCoursesSortByPaid$(): Observable<EduCourseWithTariffs[]> {
        return this.eduCoursesWithTariffs$.pipe(map((courses) => this.sortByPaid(courses)));
    }

    public eduCoursesSortByPaidBySiteId$(siteId: string): Observable<EduCourseWithTariffs[]> {
        return this.eduCoursesWithTariffsBySiteId$(siteId).pipe(map((courses) => this.sortByPaid(courses)));
    }

    private sortByPaid(courses: EduCourseWithTariffs[]): EduCourseWithTariffs[] {
        if (!courses) {
            return [];
        }

        return [...courses].sort((a, b) => {
            if (a.hasPaidTariffs && !b.hasPaidTariffs) {
                return -1;
            }

            if (!a.hasPaidTariffs && b.hasPaidTariffs) {
                return 1;
            }

            return 0;
        });
    }
}
